import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { Product } from '../../../../../shared/models/product.model';
import { CheckboxComponent } from '../../../../../shared/ui/checkbox/checkbox.component';
import { ButtonComponent } from '../../../../../shared/ui/button/button.component';
import { formatCrc } from '../../../../../shared/utils/currency.util';

@Component({
  selector: 'app-frequently-bought-together',
  imports: [CheckboxComponent, ButtonComponent],
  templateUrl: './frequently-bought-together.component.html',
  styleUrl: './frequently-bought-together.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FrequentlyBoughtTogetherComponent {
  currentProduct = input.required<Product>();
  suggestions = input.required<Product[]>();
  isAdding = input<boolean>(false);

  addBundle = output<Product[]>();

  private readonly selectedSuggestionIds = signal<Set<string>>(new Set());

  protected readonly previewItems = computed<Product[]>(() => [
    this.currentProduct(),
    ...this.suggestions(),
  ]);

  protected readonly selectedItems = computed<Product[]>(() => {
    const selected = this.selectedSuggestionIds();
    return [
      this.currentProduct(),
      ...this.suggestions().filter((s) => selected.has(String(s.id))),
    ];
  });

  protected readonly totalCount = computed(() => this.selectedItems().length);

  protected readonly totalPrice = computed(() =>
    this.selectedItems().reduce((sum, p) => sum + (p.price ?? 0), 0),
  );

  protected readonly formattedTotal = computed(() => formatCrc(this.totalPrice()));

  constructor() {
    // Re-inicializar la selección cada vez que cambian los sugeridos
    // (p. ej. al navegar a otra PDP). Por defecto todos los sugeridos
    // quedan marcados (patrón Amazon).
    effect(() => {
      const ids = this.suggestions().map((s) => String(s.id));
      this.selectedSuggestionIds.set(new Set(ids));
    });
  }

  protected isSelected(product: Product): boolean {
    return this.selectedSuggestionIds().has(String(product.id));
  }

  protected toggleSuggestion(product: Product, checked: boolean): void {
    const id = String(product.id);
    this.selectedSuggestionIds.update((set) => {
      const next = new Set(set);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  protected formatItemPrice(product: Product): string {
    return formatCrc(product.price ?? 0);
  }

  protected onAddClick(): void {
    if (this.isAdding()) return;
    this.addBundle.emit(this.selectedItems());
  }
}
